import {
    PortfolioSettlementRuleAction,
    PortfolioSettlementRuleType,
    SettlementRuleAction,
    SettlementRuleType,
} from '@mews-commander/data-access';
import { UnicodeEntity } from '@mews/enums';
import { useLocalizeText } from '@mews/framework';
import { SettlementRuleTimingProps, useSettlementRuleTimingGetter } from './SettlementRuleTiming';
import { FlatValue, PercentageValue, useSettlementRuleValueGetter } from './SettlementRuleValue';

interface SettlementRuleNameRelatedProps extends SettlementRuleTimingProps {
    name?: string | null;
    value: PercentageValue | FlatValue;
    type: `${SettlementRuleType.Manual}` | `${PortfolioSettlementRuleType.Automatic}`;
    action: `${SettlementRuleAction.ChargeCreditCard}` | `${PortfolioSettlementRuleAction.CreatePreauthorization}`;
}

type FormatConfig = 'name' | 'parameters' | 'nameAndParameters';

export const useSettlementRuleNameGetter = () => {
    const getSettlementRuleTiming = useSettlementRuleTimingGetter();
    const getSettlementRuleValue = useSettlementRuleValueGetter();
    const localizeText = useLocalizeText();

    return (
        props: SettlementRuleNameRelatedProps,
        { format, nonBreakingSpaces }: { format?: FormatConfig; nonBreakingSpaces?: boolean } = {
            format: 'name',
            nonBreakingSpaces: true,
        }
    ) => {
        const timing = getSettlementRuleTiming(props);
        const spaceChar = nonBreakingSpaces ? UnicodeEntity.NoBreakSpace : UnicodeEntity.Space;
        const value = getSettlementRuleValue(props.value);
        const paymentAttributes = localizeText({
            textKey: `PaymentPolicyName${props.type}${props.action}` as Mews.TextKey,
        });
        const parameters = `${timing}${UnicodeEntity.Colon}${spaceChar}${value}${spaceChar}${paymentAttributes}`;

        switch (format) {
            case 'parameters':
                return parameters;
            case 'nameAndParameters':
                return `${parameters}${
                    props.name
                        ? `${spaceChar}${UnicodeEntity.LeftParenthesis}${props.name}${UnicodeEntity.RightParenthesis}`
                        : ''
                }`;
            case 'name':
            default:
                return props.name || parameters;
        }
    };
};

interface SettlementRuleNameProps extends SettlementRuleNameRelatedProps {
    format?: FormatConfig;
    nonBreakingSpaces?: boolean;
}

export const SettlementRuleName = ({ format, ...other }: SettlementRuleNameProps) => {
    const getSettlementRuleName = useSettlementRuleNameGetter();
    return <span>{getSettlementRuleName(other, { format })}</span>;
};
